import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Drawer,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import AddFilledIcon from '@mui/icons-material/Add';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import axios from '../../api/axios';
import { PlansTableLayout } from './PlansTableLayout';
import '../../index.css';
import { WithPermission } from '../../Auth/WithPermission';
import AddPlan from './AddPlan';
import { useNavigate } from 'react-router-dom';
import * as signalR from "@microsoft/signalr";

const GET_BUILDERS = "/builder/";
const CREATE_PLAN = "/plan/";

export const Plans = () => {
  const navigate = useNavigate();

  const [selectedBuilderId, setSelectedBuilderId] = useState("");

  const [highlightPlanId, setHighlightPlanId] = useState(null);

  const [openModal, setOpenModal] = useState(false);

  const [builders, setBuilders] = useState([]);
  const [buildersList, setBuildersList] = useState([])

  const [fileBadges, setFileBadges] = useState({
    ready: 0,
    processing: 0,
    error: 0
  });

  const [loading, setLoading] = useState(false)
  const devMode= process.env.REACT_APP_DEV_MODE;

  useEffect(() => {
    let connection;

    const start = async () => {
      // negotiate
      const linkRes = devMode === "PRODUCTION"
                      ? "https://api.ocubuild.com/negotiate/?code=aKCFM45cBJI3BtflqLbQA4E2f8RMktLPebxeMPT37NgoAzFu3rFEWA%3D%3D"
                      : "https://dev-api.ocubuild.com/negotiate/?code=GFZrhB9U0Kn9ZtwIyFkupd2NSVlpFNNUhZW5nHNsbhP0AzFuwJuC1A%3D%3D";

      const res = await fetch(linkRes);
      const info = await res.json();

      connection = new signalR.HubConnectionBuilder()
        .withUrl(info.url, { accessTokenFactory: () => info.accessToken })
        .withAutomaticReconnect()
        .build();

      // receive messages
      connection.on("fileStatusUpdate", (data) => {
          console.log("SignalR message:", data);
          console.log("status:", data.status);
          console.log("cadFileId:", data.cadFileId);
          console.log("builderId:", data.builderId);
          console.log("planId:", data.planId);

          setBuildersList(prevGroupedPlans =>
            prevGroupedPlans.map(builder => {
              // Only update the builder if it matches the selected builder
              if (builder.id === data.builderId) {
                const updatedPlans = builder.plans.map(plan => {
                  if (plan.id === data.planId) {
                    return {
                      ...plan,
                      fileStatus: data.status
                    };
                  }
                  return plan;
                });

                return { ...builder, plans: updatedPlans };
              }
              return builder;
            })
          );
          sumFileBadges(selectedBuilderId); // Update badge counts for the builder
      });

      await connection.start()
                .then(() => console.log("SignalR connected successfully."))
                .catch(err => console.error("Connection failed: ", err));    
    };

    start();

    return () => {
      if (connection) connection.stop();
    };

}, []);

  function sumFileBadges(selectedBuilderId) {
    const builder = buildersList.find(b => b.id === selectedBuilderId);
    
    if (!builder) {
      // no builder found -> zero counts
      setFileBadges({ ready: 0, processing: 0, error: 0 });
      return;
    }

    const counts = { ready: 0, processing: 0, error: 0 };
    (builder.plans || []).forEach(plan => {
      if (plan.archived || plan.isPlaceholder) return;
      const s = (plan.fileStatus || "").toLowerCase();
      if (s === "ready") counts.ready += 1;
      else if (s === "processing") counts.processing += 1;
      else if (s === "error") counts.error += 1;
    });

    setFileBadges(counts);
  }

  useEffect(() => {
    sumFileBadges(selectedBuilderId)
  }, [selectedBuilderId])

  useEffect(() => {
    sumFileBadges(selectedBuilderId)
  }, [buildersList])

  useEffect(() => {
    setLoading(true)
    axios.post(GET_BUILDERS, {
      token: localStorage.getItem('token'),
      getBuilders: 'true',
    })
    .then((response) => {
      let buildersList = response.data?.message?.builders || [];

      buildersList = buildersList
        .filter(b => typeof b.builderName === 'string' && (b.archived === false || b.archived === undefined))
        .sort((a, b) => {
          const nameA = a.builderName;
          const nameB = b.builderName;

          const numA = parseInt(nameA.match(/\d+/)?.[0] || "0", 10);
          const numB = parseInt(nameB.match(/\d+/)?.[0] || "0", 10);

          // First compare the non-numeric part
          const baseA = nameA.replace(/\d+/g, '').trim();
          const baseB = nameB.replace(/\d+/g, '').trim();

          if (baseA === baseB) {
            return numA - numB; // Numeric comparison
          }

          return baseA.localeCompare(baseB); // Alphabetical comparison of text prefix
        });

      setBuilders(buildersList);

    })
    .catch((error) => {
      console.error('Error fetching builders:', error);
    });

    axios.post(CREATE_PLAN, {
      token: localStorage.getItem('token'),
      returnListOfPlans: 'true'
    })
    .then((response) => {
      //buildStageList.sort((a, b) => a.sortOrder - b.sortOrder).map(stage => stage.buildStageName);
      const plansList = response.data?.message?.listOfPlans || []
      setBuildersList(transformData(plansList));
      // setBuildersList([])
    })
    .catch((error) => {
      console.error('Error fetching build stages:', error);
    }).finally(() => {
      setLoading(false)
    })
  }, []);

  const transformData = (plansList) => {
    if (!plansList) return [];

    const builderMap = new Map();

    plansList.forEach(plan => {
      const builderId = plan.builder?.id || "unknown";
      const builderName = plan.builder?.builderName || plan.builder?.name || "Unknown Builder";
      const archivedBuilder = plan.builder?.archived || false;
      const projectId = plan.project?.id || "";

      // Get or create the builder entry
      if (!builderMap.has(builderId)) {
        builderMap.set(builderId, {
          id: builderId,
          name: builderName,
          plans: [],
          isEmpty: plan.type === 'empty-builder',
          isArchivedBuilder: archivedBuilder
        });
      }

      const builder = builderMap.get(builderId);

      // Handle empty builders
      if (plan.type === 'empty-builder') {
        const placeholderPlan = {
          id: `empty-${builderId}`,
          isPlaceholder: true,
          isEmpty: true,
          project: "No Project Assigned",
          projectId: projectId,
          projectSortKey: "No Project Assigned",
          lot: "N/A",
          lotSpecific: false,
          planNote: "",
          nonstandardAnchor: false,
          anchorNote: "",
          planNumber: "No Plans",
          elevation: "N/A",
          layout: "",
          options: [],
          buildStages: [],
          lastModifiedCadFileDateTime: new Date(0),
          uploadedCadFileDateTime: new Date(0),
          uploadedCadFileBy: "",
          fileName: "",
          fileStatus: "processing",
          lastModifiedTimestamp: 0,
          isArchivedProject: plan.project?.archived,
          isArchivedBuilder: archivedBuilder,
          archivedBy: "",
          archivingUser: null,
          planModifiedDateTime: plan?.planModifiedDateTime || "",
          planNumberNum: 999999,
          elevationNum: 999999
        };

        builder.plans.push(placeholderPlan);
        return;
      }

      // Handle empty projects
      if (plan.type === 'empty-project') {
        const placeholderPlan = {
          id: `empty-project-${plan.projectId}`,
          isPlaceholder: true,
          isEmpty: false,
          isEmptyProject: true,
          project: plan.project?.projectName || plan.project?.name || "",
          projectId: projectId,
          projectSortKey: plan.project?.projectName || plan.project?.name || "",
          lot: "N/A",
          lotSpecific: false,
          planNote: "",
          nonstandardAnchor: false,
          anchorNote: "",
          planNumber: "No Plans",
          elevation: "N/A",
          layout: "",
          options: [],
          buildStages: [],
          lastModifiedCadFileDateTime: new Date(0),
          uploadedCadFileDateTime: new Date(0),
          uploadedCadFileBy: "",
          fileName: "",
          fileStatus: "processing",
          lastModifiedTimestamp: 0,
          isArchivedProject: plan.project?.archived,
          isArchivedBuilder: archivedBuilder,
          archivedBy: "",
          archivingUser: null,
          planModifiedDateTime: plan?.planModifiedDateTime || "",
          planNumberNum: 999999,
          elevationNum: 999999
        };

        builder.plans.push(placeholderPlan);
        return;
      }

      // Handle archived plans - include them but mark them as archived
      if (plan.archived) {
        const archivedPlan = {
          id: plan.id || "",
          isPlaceholder: false,
          isEmpty: false,
          isArchived: true, // Add this flag to identify archived plans
          project: plan.project?.projectName || plan.project?.name || "No Project Assigned",
          projectId: projectId,
          projectSortKey: plan.project?.projectName || plan.project?.name || "",
          isArchivedProject: plan.project?.archived,
          isArchivedBuilder: archivedBuilder,
          lot: plan.lot_Name || "",
          lotSpecific: plan.lot_Specific || false,
          planNote: plan.plan_Note || "",
          nonstandardAnchor: plan.nonstandard_Anchor || false,
          anchorNote: plan.anchor_Note || "",
          planNumber: plan.planNumber?.planNumberName || "",
          elevation: plan.elevation?.elevationName || "",
          layout: plan.layout || "",
          options: plan.planOptions || [],
          buildStages: plan.buildStages || [],
          lastModifiedCadFileDateTime: new Date(0),
          uploadedCadFileDateTime: new Date(0),
          uploadedCadFileBy: "",
          fileName: "",
          fileStatus: "processing",
          lastModifiedTimestamp: 0,
          archived: true,
          archivedBy: plan.archivedBy || "",
          archivingUser: plan.archivingUser || null,
          planModifiedDateTime: plan?.planModifiedDateTime || "",
          planNumberNum: parseInt(plan.planNumber?.planNumberName?.replace(/\D/g, "") || "0", 10),
          elevationNum: parseInt(plan.elevation?.elevationName?.replace(/\D/g, "") || "0", 10)
        };

        builder.plans.push(archivedPlan);
        return;
      }

      // Handle regular non-archived plans
      let cadFilelastModifiedDate = new Date(0);
      let cadFileUploadedDate = new Date(0);

      if (!plan.isPlaceholder) {
        cadFilelastModifiedDate = plan.cadFiles?.fileDateTime ? new Date(plan.cadFiles.fileDateTime) : new Date(0);
        cadFileUploadedDate = plan.cadFiles?.uploadedDateAndTime ? new Date(plan.cadFiles.uploadedDateAndTime) : new Date(0);
      }

      const formattedPlan = {
        id: plan.id || "",
        isPlaceholder: plan.isPlaceholder || false,
        isEmpty: false,
        project: plan.project?.projectName || plan.project?.name || "No Project Assigned",
        projectId: projectId,
        projectSortKey: plan.project?.projectName || plan.project?.name || "",
        isArchivedProject: plan.project?.archived,
        lot: plan.lot_Name || "",
        lotSpecific: plan.lot_Specific || false,
        planNote: plan.plan_Note || "",
        nonstandardAnchor: plan.nonstandard_Anchor || false,
        anchorNote: plan.anchor_Note || "",
        planNumber: plan.planNumber?.planNumberName || "",
        elevation: plan.elevation?.elevationName || "",
        layout: plan.layout || "",
        options: plan.planOptions || [],
        buildStages: plan.buildStages || [],
        lastModifiedCadFileDateTime: cadFilelastModifiedDate,
        uploadedCadFileDateTime: cadFileUploadedDate,
        uploadedCadFileBy: plan.cadFiles?.uploadedBy,
        fileName: plan.cadFiles?.fileName,
        fileStatus: plan.cadFiles?.fileStatus || "processing",
        lastModifiedTimestamp: cadFilelastModifiedDate.getTime(),
        archived: plan.archived || false,
        archivedBy: plan.archivedBy || "",
        archivingUser: plan.archivingUser || null,
        planModifiedDateTime: plan?.planModifiedDateTime || "",
        planNumberNum: parseInt(plan.planNumber?.planNumberName?.replace(/\D/g, "") || "0", 10),
        elevationNum: parseInt(plan.elevation?.elevationName?.replace(/\D/g, "") || "0", 10),
        pdfFiles: plan.pdfFiles || null,
        builderId: plan.builderId,
        cadFileId: plan.cadFileId,
        builder: plan.builder
      };

      builder.plans.push(formattedPlan);
    });

    // Convert the map to an array and sort
    return Array.from(builderMap.values())
      .map(builder => ({
        ...builder,
        plans: [...builder.plans].sort((a, b) => {
          // Put placeholders and archived plans at the end
          if (a.isPlaceholder && !b.isPlaceholder) return 1;
          if (!a.isPlaceholder&& b.isPlaceholder) return -1;
          if (a.isEmpty || b.isEmpty) return 0;

          // Regular sorting for active plans
          const projectCompare = naturalSort(a.projectSortKey, b.projectSortKey);
          if (projectCompare !== 0) return projectCompare;

          const planNumberCompare = naturalSort(a.planNumber, b.planNumber);
          if (planNumberCompare !== 0) return planNumberCompare;

          const elevationCompare = naturalSort(a.elevation, b.elevation);
          if (elevationCompare !== 0) return elevationCompare;

          return b.lastModifiedTimestamp - a.lastModifiedTimestamp;
        })
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  const addNewPlanToBuildersList = (planData, cadFile, planOptionsForLists, buildStagesForLists, pdfFile) => {

    console.log(planData)
    console.log(cadFile)

    const formattedPlan = {
      id: planData.planId,
      isPlaceholder: false,
      isEmpty: false,
      project: planData?.project || "No Project Assigned",
      projectSortKey: planData?.project || "",
      projectId: planData?.projectId || "",
      isArchivedProject: false,
      lot: planData.lotLocation,
      lotSpecific: planData.lotSpecific,
      planNote: planData.planNotes,
      nonstandardAnchor: planData.nonStandard,
      anchorNote: planData.anchorNotes,
      planNumber: planData.planNumber?.planNumberName || "",
      elevation: planData?.elevation || "",
      layout: planData.layout,
      options: planOptionsForLists,
      buildStages: buildStagesForLists,
      lastModifiedCadFileDateTime: new Date(cadFile.savedFileModifiedDate),
      uploadedCadFileDateTime: new Date(cadFile.savedFileUploadedDate),
      uploadedCadFileBy: cadFile.savedFileUploadedBy,
      fileName: cadFile.name,
      fileStatus: cadFile?.status || "processing",
      lastModifiedTimestamp: new Date(cadFile.savedFileUploadedDate).getTime(),
      archived: false,
      archivedBy: "",
      archivingUser: null,
      planModifiedDateTime: new Date(),
      planNumberNum: parseInt(planData.planNumber?.planNumberName?.replace(/\D/g, "") || "0", 10),
      elevationNum: planData.elevation?.elevationName ? parseInt(planData.elevation.elevationName.replace(/\D/g, "") || "0", 10) : 0,
      pdfFiles: pdfFile ? {
        id: pdfFile.savedFileId,
        fileName: pdfFile.name,
        fileDateTime: pdfFile.savedFileModifiedDate,
        uploadedBy: pdfFile.savedFileUploadedBy,
        uploadedDateAndTime: pdfFile.savedFileUploadedDate
      } : null,
      builderId: planData.builder?.id,
      builder: planData.builder
    };

    setBuildersList((prev) => {
      const builderId = planData.builder?.id || "unknown";
      const builderName = planData.builder?.builderName || planData.builder?.name || "Unknown Builder";
      const projectId = planData?.projectId || "";
      const placeholderId = `empty-project-${projectId}`;

      const builderIndex = prev.findIndex(b => b.id === builderId);

      if (builderIndex > -1) {
        const updatedBuilder = { ...prev[builderIndex] };
        
        // Check if there's a placeholder to replace
        const placeholderIndex = updatedBuilder.plans.findIndex(p => p.id === placeholderId);
        
        if (placeholderIndex > -1) {
          // Replace the placeholder with the new plan
          updatedBuilder.plans = [...updatedBuilder.plans];
          updatedBuilder.plans[placeholderIndex] = formattedPlan;
        } else {
          // No placeholder found, just add the new plan
          updatedBuilder.plans = [...updatedBuilder.plans, formattedPlan];
        }
        
        // Sort the plans
        updatedBuilder.plans.sort((a, b) => {
          const projectCompare = naturalSort(a.projectSortKey, b.projectSortKey);
          if (projectCompare !== 0) return projectCompare;

          const planNumberCompare = naturalSort(a.planNumber, b.planNumber);
          if (planNumberCompare !== 0) return planNumberCompare;

          const elevationCompare = naturalSort(a.elevation, b.elevation);
          if (elevationCompare !== 0) return elevationCompare;

          return b.lastModifiedTimestamp - a.lastModifiedTimestamp;
        });

        const newList = [...prev];
        newList[builderIndex] = updatedBuilder;
        return newList;
      } else {
        // Create new builder entry
        return [
          ...prev,
          {
            id: builderId,
            name: builderName,
            plans: [formattedPlan],
            isEmpty: false
          }
        ].sort((a, b) => a.name.localeCompare(b.name));
      }
    });
  };

  const naturalSort = (a, b) => {
    return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
  };

  const handleArchivePlan = async (planId) => {
    try {
      if (!planId) {
        console.error("No plan selected to archive.");
        return;
      }

      const res = await axios.post(CREATE_PLAN, {
        token: localStorage.getItem("token"),
        planId,
        archivePlan: true,
      });

      const { code, message } = res.data;

      if (code === 0) {
        const updatedBuilders = buildersList.map(builder => ({
          ...builder,
          plans: builder.plans.map(plan =>
            plan.id === planId ? { ...plan, archived: true } : plan
          )
        }));
        setBuildersList(updatedBuilders);
      } else {
        console.error("Archiving failed:", message);
      }
    } catch (err) {
      console.error("Error archiving plan:", err);
    }
  };

return (
  <>
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {loading ? (
        <Box 
          sx={{ 
            display: "flex", 
            justifyContent: "center", 
            alignItems: "center", 
            minHeight: "400px"
          }}
        >
          <CircularProgress/>
        </Box>
      ) :
        buildersList.length === 0 ? (
          <>
          <Drawer
            variant="permanent"
            sx={{
              width: 200,
              flexShrink: 0,
              [`& .MuiDrawer-paper`]: {
                width: 200,
                boxSizing: 'border-box',
                top: 'var(--app-header-height)',
                height: 'calc(100vh - var(--app-header-height))',
                borderRight: '1px solid rgba(0,0,0,0.08)',
              },
              zIndex: 1
            }}
          >
            <Box sx={{ overflow: 'auto', pt: 4, paddingX: "5%" }}>
              <Typography sx={{ px: 2, pb: 1, fontSize: 12, color: 'text.secondary', fontWeight: "bold" }}>
                SELECT BUILDER
              </Typography>
  
              <Divider sx={{ my: 1 }} />
  
              <List>
                <ListItem disablePadding>
                  <ListItemButton
                    sx={{ color: "#1976D2" }}
                    onClick={() => navigate('/archived-plans')}
                  >
                    <ListItemIcon sx={{ minWidth: "auto" }}>
                      <Inventory2Icon sx={{ color: "#1976D2", fontSize: 20 }}/>
                    </ListItemIcon>
                    
                    <ListItemText
                      sx={{ marginLeft: "5px" }}
                      primary="VIEW ARCHIVE"
                      primaryTypographyProps={{ fontWeight: 500, fontSize: 14 }}
                    />
                  </ListItemButton>
                </ListItem>
              </List>
            </Box>
          </Drawer>

          <WithPermission rolesRequired={["admin", "superadmin", "useredit"]}>
            <div style={{ 
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "700px"
            }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Add your first plan to get started.
              </Typography>
              <Button
                data-testid="add-plan-button"
                variant="contained"
                startIcon={<AddFilledIcon />}
                onClick={() => setOpenModal(true)}
                disabled={openModal}
                sx={{
                  width: "144px",
                  height: "42px",
                  padding: "8px 22px",
                  fontSize: "16px",
                  textTransform: "none",
                  borderRadius: "8px",
                  backgroundColor: "#1976D2",
                  "&:hover": {
                    backgroundColor: "#1565c0",
                  },
                }}
              >
                Add Plan
              </Button>
            </div>
          </WithPermission>
        </>
      ) : (
        <PlansTableLayout
          builders={buildersList}
          setBuilders={setBuildersList}
          addNewPlanToBuildersList={addNewPlanToBuildersList}
          addPlan={() => setOpenModal(true)}
          setOpenModal={setOpenModal}
          handleArchivePlan={handleArchivePlan}
          highlightPlanId={highlightPlanId}
          setHighlightPlanId={setHighlightPlanId}
          selectedBuilderId={selectedBuilderId}
          setSelectedBuilderId={setSelectedBuilderId}
          fileBadges={fileBadges}
          sumFileBadges={sumFileBadges}
        />
      )
    }
    </Box>

    <AddPlan
      openModal={openModal}
      setOpenModal={setOpenModal}
      addNewPlanToBuildersList={addNewPlanToBuildersList}
      tableBuilders={builders}
      setHighlightPlanId={setHighlightPlanId}
      setSelectedBuilderId={setSelectedBuilderId}
      selectedBuilderId={selectedBuilderId}
    />
    
  </>
);

};
